import type { LocationEntry } from '../../hooks/useGetLocations';

/**
 * How settings records store a location assignment: the named locations, or
 * the 'all' sentinel meaning org-wide. Vendors, DRP carriers and other records
 * all use this shape, so the conversions to and from the id lists the pickers
 * work in live here rather than in each table.
 */
export type LocationList = Array<{ _id: string; title: string }> | 'all';

export const toLocationIds = (value: LocationList): Array<string> | 'all' =>
    value === 'all' ? 'all' : value.map((location) => location._id);

/**
 * Back to the stored shape. Ids the org no longer has are dropped, since the
 * saved value carries titles that only the locations list knows.
 */
export const toLocationList = (
    value: Array<string> | 'all',
    locations: LocationEntry[],
): LocationList => {
    if (value === 'all') return 'all';
    return value
        .map((id) => locations.find((location) => location._id === id))
        .filter((location): location is LocationEntry => !!location)
        .map((location) => ({ _id: location._id as string, title: location.title }));
};

/**
 * The id out of a stored entry. Records written by older code — and raw BSON
 * that reached the client without being flattened — nest it, so a plain hex
 * string is only the common case.
 */
const readLocationId = (value: unknown): string | undefined => {
    if (typeof value === 'string') return value;
    if (!value || typeof value !== 'object') return undefined;
    const record = value as Record<string, unknown>;
    if (typeof record.$oid === 'string') return record.$oid;
    if (record._id !== undefined) return readLocationId(record._id);
    if (typeof record.toString === 'function') {
        const text = String(record);
        return /^[a-f\d]{24}$/i.test(text) ? text : undefined;
    }
    return undefined;
};

const readLocationTitle = (value: unknown): string | undefined => {
    if (!value || typeof value !== 'object') return undefined;
    const title = (value as Record<string, unknown>).title;
    return typeof title === 'string' ? title : undefined;
};

const normalizeTitle = (title: string): string => title.trim().toLowerCase();

/**
 * The 'all' sentinel spread into its characters. Some vendor records were saved
 * this way, which reads as three locations that match nothing — the shape this
 * resolver exists to catch. Location ids are 24-char hex, so a list of single
 * characters spelling "all" can only be the mangled sentinel.
 */
const isSpreadSentinel = (value: Array<unknown>): boolean =>
    value.length === 3 &&
    value.every((entry) => typeof entry === 'string') &&
    (value as Array<string>).join('') === 'all';

/**
 * Line a stored assignment up against the org's locations, which is what every
 * picker works in. An entry only counts once it matches a real shop: the id
 * first, then the title it was saved with, since a location that was recreated
 * keeps its name but not its id. Unmatched entries are dropped, so the count a
 * table shows always equals the boxes its editor ticks.
 */
export const resolveLocationList = (
    value: LocationList | Array<unknown> | 'all' | undefined | null,
    locations: LocationEntry[],
): LocationList => {
    if (value === 'all') return 'all';
    if (!Array.isArray(value)) return [];
    if (isSpreadSentinel(value)) return 'all';

    const shops = locations.filter((location) => location._id !== 'all');
    const byId = new Map(shops.map((shop) => [shop._id as string, shop]));
    const byTitle = new Map<string, LocationEntry | null>();
    shops.forEach((shop) => {
        const key = normalizeTitle(shop.title);
        // Two shops with the same name make the title useless as a fallback.
        byTitle.set(key, byTitle.has(key) ? null : shop);
    });

    const resolved: Array<{ _id: string; title: string }> = [];
    const seen = new Set<string>();
    value.forEach((entry) => {
        const id = readLocationId(entry);
        const title = readLocationTitle(entry);
        const match =
            (id ? byId.get(id) : undefined) ??
            (title ? byTitle.get(normalizeTitle(title)) ?? undefined : undefined);
        if (!match) return;
        const matchId = match._id as string;
        if (seen.has(matchId)) return;
        seen.add(matchId);
        resolved.push({ _id: matchId, title: match.title });
    });

    return resolved;
};

export const isSameLocationList = (a: LocationList, b: LocationList): boolean => {
    if (a === 'all' || b === 'all') return a === b;
    if (a.length !== b.length) return false;
    const other = new Set(b.map((location) => location._id));
    return a.every((location) => other.has(location._id));
};

/** "All Locations", the one shop's name, or a count. */
export const formatLocationList = (value: LocationList): string => {
    if (value === 'all') return 'All Locations';
    if (value.length === 0) return 'None';
    if (value.length === 1) return value[0].title;
    return `${value.length} locations`;
};
